import cx from 'classnames';
import React, { ReactElement, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClientProductSummary,
  ProductStatus,
  SprintProductStatus,
  useClientOwnProducts,
  useClientProductTime,
  useClientSprintProducts,
} from '@data';
import { useOrganizationId } from '@app/utils/hooks';
import { clientProductPath, clientProductReportsPath } from '@app/constants/url';
import { Loader } from '@ui';
import { Archive, ArrowRight, Cross, Hammer, LayoutGrid, type LucideIcon, X } from 'lucide-react';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
// Straight from the module, not the barrel: the barrel drags a cycle in behind it, as its own note says.
import { hasDispute } from '@app/components/core/approvalSummary/UnreadCommentDot';
import { cardBleedClass } from '@app/constants/styles';
import { buildTimeReport, timeReportWindow } from '../product/sources/timeReport';
import SentReports from '../sentReports';

// Products are shown active first, then in-support, then archived (deactivated last).
const STATUS_ORDER: Record<string, number> = {
  [ProductStatus.active]: 0,
  [ProductStatus.support]: 1,
  [ProductStatus.archived]: 2,
  [ProductStatus.deactivated]: 3,
};

// A symbol per state: tools while active, a health cross for support, an X for deactivated, a box for archived.
const STATUS_ICON: Record<string, LucideIcon> = {
  [ProductStatus.active]: Hammer,
  [ProductStatus.support]: Cross,
  [ProductStatus.deactivated]: X,
  [ProductStatus.archived]: Archive,
};

// One product's card. It fetches its own reported time, so the hours it leads with are the product's real
// figures — the same source (and so the same numbers) as the product dashboard's delivery report. Sprint
// counts are the caller's, taken from the one org-wide sprint query.
const ProductCard = ({
  product,
  organizationId,
  toApprove,
  sprintsCompleted,
  onOpen,
}: {
  product: ClientProductSummary;
  organizationId: string;
  toApprove: number;
  sprintsCompleted: number;
  onOpen: (e: React.MouseEvent, to: string) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const reportWindow = useMemo(() => timeReportWindow(), []);
  const { data } = useClientProductTime(organizationId, product.id, reportWindow);
  const report = buildTimeReport(data?.items ?? []);
  // The latest sprint carrying reported time — a client only ever sees sprints we've closed.
  const hoursLastSprint = Math.round(report.hoursSeries.findLast(hours => hours > 0) ?? 0);

  const productTo = clientProductPath(organizationId, product.id);
  // Approve always leads to the product's sprints overview, where each sprint carries its Approve action.
  const approveTo = clientProductReportsPath(organizationId, product.id);

  return (
    // Whole card lifts as one unit on hover (the shared clickable-card treatment). A stretched overlay
    // <Link> is the general click target (→ product); the approve link and everything else sit above it,
    // with only the approve link capturing its own clicks (→ approval). The footer is a non-interactive
    // "opens the product" hint.
    <div
      className={cx(
        'group relative flex min-h-72 flex-col rounded-xl border border-border bg-card transition-shadow hover:shadow-md max-md:min-h-0',
        cardBleedClass,
      )}
    >
      <Link
        to={productTo}
        onClick={e => onOpen(e, productTo)}
        aria-label={product.name}
        className="absolute inset-0 z-0 rounded-xl"
      />

      <div className="pointer-events-none relative z-10 flex flex-1 flex-col p-6 max-md:p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-[25px] font-bold leading-none text-foreground">{product.name}</p>
          {/* The status word is right-aligned in its own text column, with the icon in a second column,
              so the icon never extends the text's right edge. */}
          <div className="grid shrink-0 grid-cols-[auto_auto] items-center gap-x-1.5">
            {product.status &&
              (() => {
                const StatusIcon = STATUS_ICON[product.status] ?? Hammer;
                return (
                  <>
                    <span className="text-right text-xs font-semibold uppercase leading-none tracking-wider text-muted-foreground">
                      {t(`tabs.${product.status}`)}
                    </span>
                    <StatusIcon className="size-[12.5px] text-muted-foreground" />
                  </>
                );
              })()}
          </div>
        </div>

        {/* The figure on the first row; the approve link sits with the summary lines below, so it
            reads as one of them rather than as a second figure. */}
        <div className="mt-5">
          <div className="rounded-lg bg-muted/60 p-4 transition-colors group-hover:bg-muted-foreground/10">
            <p className="text-xl font-bold leading-none tabular-nums text-foreground">{hoursLastSprint}h</p>
            <p className="mt-1.5 text-xs text-muted-foreground">{t('metricHoursLabel')}</p>
          </div>
        </div>

        <div className="mt-3 space-y-1">
          {/* Its own click target (→ approval), so it keeps `pointer-events-auto` above the card's
              overlay link. `w-fit` keeps the underline on the words instead of the full column. */}
          {toApprove > 0 && (
            <Link
              to={approveTo}
              onClick={e => onOpen(e, approveTo)}
              className="pointer-events-auto inline-flex w-fit text-sm font-medium text-muted-foreground underline transition-colors hover:text-foreground"
            >
              {t('nToApprove', { count: toApprove })}
            </Link>
          )}
          <p className="text-sm font-medium text-muted-foreground">
            {t('nSprintsCompleted', { count: sprintsCompleted })}
          </p>
        </div>
      </div>

      {/* Footer hint — the overlay handles the click, so this is display only. The divider is inset
          (mx-6, not full-width px-6) so it doesn't read as slicing the card in two. */}
      <div className="pointer-events-none relative z-10 mx-6 flex items-center justify-between border-t border-border py-4 text-sm font-medium text-muted-foreground max-md:mx-4">
        {t('clickToView')}
        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </div>
  );
};

// Client landing: the products an admin linked to this client. A single product skips straight in;
// the rest see a card each.
const ClientProducts = (): ReactElement => {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const organizationId = useOrganizationId();
  const { data, isLoading } = useClientOwnProducts(organizationId);

  // Open a product with a fade + right→left swipe. Let modified clicks through so open-in-new-tab works.
  const openProduct = (e: React.MouseEvent, to: string): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    transitionNavigate(to, 'forward');
  };

  const statuses = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];
  const { data: sprintProducts, isLoading: isReportsLoading } = useClientSprintProducts(organizationId, { statuses });

  if (!organizationId || isLoading || isReportsLoading) {
    return <Loader />;
  }

  const products = [...(data?.items ?? [])].sort(
    (a, b) =>
      (STATUS_ORDER[a.status ?? ''] ?? 99) - (STATUS_ORDER[b.status ?? ''] ?? 99) ||
      (a.name ?? '').localeCompare(b.name ?? ''),
  );

  if (products.length === 1) {
    return <Navigate to={`products/${products[0].id}`} replace />;
  }

  if (!products.length) {
    // Nobody gave them a product, but reports have been mailed to them: those reports are the whole of
    // what they may read, so the landing is the list of them rather than a claim that they have nothing.
    if (sprintProducts?.items?.length) {
      return <SentReports />;
    }

    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <div className="flex items-center justify-center rounded-full bg-card p-8">
          <LayoutGrid className="size-12 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-bold text-foreground">{t('noProductsYet')}</h2>
        <p className="text-sm text-muted-foreground">{t('noProductsYetHint')}</p>
      </div>
    );
  }

  // How many distinct sprints are still awaiting the client's approval (delivered but not yet approved).
  // A disputed report is not one of them: the dispute is the client's answer, so counting it chases them
  // for one they have already given. `hasDispute` is the same rule the report's own actions read, and it
  // stops holding once we answer the remark, at which point the report is theirs again.
  const toApproveCountFor = (productId: string): number =>
    new Set(
      (sprintProducts?.items ?? [])
        .filter(
          item => item.product?.id === productId && item.status === SprintProductStatus.closed && !hasDispute(item),
        )
        .map(item => item.sprint?.id)
        .filter(Boolean),
    ).size;

  // Total distinct sprints delivered for a product (closed/approved/invoiced) — the "completed" count.
  const sprintsCompletedFor = (productId: string): number =>
    new Set(
      (sprintProducts?.items ?? [])
        .filter(item => item.product?.id === productId)
        .map(item => item.sprint?.id)
        .filter(Boolean),
    ).size;

  return (
    <div className="grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {products.map(product => (
        <ProductCard
          key={product.id}
          product={product}
          organizationId={organizationId}
          toApprove={toApproveCountFor(product.id)}
          sprintsCompleted={sprintsCompletedFor(product.id)}
          onOpen={openProduct}
        />
      ))}
    </div>
  );
};

export default ClientProducts;
